<template>
  <div class="py-4 glass-frosted h-full flex flex-col rounded-8xl">
    <p class="text-black-white text-l-medium px-4 pb-3">
      {{
        isMacOS
          ? $t('parameters.pointerSpeed.titleMac')
          : $t('parameters.pointerSpeed.titleWindows')
      }}
    </p>

    <div
      ref="trackRef"
      class="relative flex-1 border-b border-n-light-hover mb-1 cursor-pointer select-none"
      @mousedown="onPointerDown"
    >
      <!-- 梯形背景 -->
      <div
        class="absolute inset-0"
        :style="{
          clipPath: `polygon(0% 100%, 0% ${TOP_EDGE_LEFT}%, 100% ${TOP_EDGE_RIGHT}%, 100% 100%)`,
          background: 'linear-gradient(180deg, rgba(185,243,255,0.6) 0%, rgba(255,255,255,0) 100%)',
        }"
      />

      <!-- 梯形上边沿线 -->
      <svg class="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
        <line
          x1="0%"
          :y1="`${TOP_EDGE_LEFT}%`"
          x2="100%"
          :y2="`${TOP_EDGE_RIGHT}%`"
          stroke="rgba(255,255,255,0.15)"
          stroke-width="1"
        />
      </svg>

      <!-- 竖线 Thumb -->
      <div
        class="absolute top-0 bottom-0 w-px bg-black-white pointer-events-none"
        :style="{ left: `${leftPercent}%` }"
      >
        <!-- 圆点 -->
        <div
          class="absolute left-1/2 w-2.5 h-2.5 rounded-full bg-black-white shadow-md shadow-white/30"
          :style="{ top: `${dotTop}%`, transform: 'translate(-50%, -50%)' }"
        />
      </div>

      <!-- 数值标签 -->
      <div
        class="absolute pointer-events-none flex items-center"
        :style="{
          left: `${leftPercent}%`,
          top: `${dotTop}%`,
          transform: 'translate(calc(-100% - 10px), calc(-50% + 5px))',
        }"
      >
        <span class="text-[30px] text-brand-blue1 font-[Barlow] tabular-nums">
          {{ value }}
        </span>
      </div>
    </div>

    <!-- 刻度尺 -->
    <div class="relative h-6 shrink-0">
      <span
        v-for="tick in ticks"
        :key="tick"
        class="absolute text-s-medium text-text-glass-default dark:text-neutral-500 tabular-nums -translate-x-1/2"
        :style="{ left: `${getTickLeft(tick)}%` }"
      >
        {{ tick }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue'
  import type { ModuleSize } from '../utils/types'
  import type { ParametersContext } from '../../composables/useParameters'
  import { isMacOS } from '@/utils/ua'

  const props = defineProps<{
    parameters: ParametersContext
    size?: ModuleSize
  }>()

  const ctx = props.parameters
  const value = computed(() => ctx.state.pointerSpeed.value)

  /** 最小值 */
  const min = 1
  /** 最大值 */
  const max = 20

  /** 轴线两端内边距百分比（首尾刻度不贴边） */
  const AXIS_PADDING_PCT = 3

  /** 当前值在轴线有效区间内的百分比位置 (0~1) */
  const rawPercent = computed(() => (value.value - min) / (max - min))

  /** 映射到容器实际 left 百分比（考虑首尾内边距） */
  const leftPercent = computed(() => {
    return AXIS_PADDING_PCT + rawPercent.value * (100 - 2 * AXIS_PADDING_PCT)
  })

  /**
   * 梯形上边沿的 Y 坐标（百分比，从顶部算）
   * 左侧上边沿接近底部（88%），右侧接近顶部（12%）
   */
  const TOP_EDGE_LEFT = 60
  const TOP_EDGE_RIGHT = 6

  /** 圆点的 Y 位置百分比（从容器顶部算，基于线上实际 X 位置插值） */
  const dotTop = computed(() => {
    return TOP_EDGE_LEFT + (TOP_EDGE_RIGHT - TOP_EDGE_LEFT) * (leftPercent.value / 100)
  })

  /** 刻度值列表 */
  const ticks = computed(() => {
    const result: number[] = []
    for (let i = min; i <= max; i++) {
      result.push(i)
    }
    return result
  })

  /** 计算某个 tick 的 left 百分比 */
  function getTickLeft(tick: number): number {
    const pct = (tick - min) / (max - min)
    return AXIS_PADDING_PCT + pct * (100 - 2 * AXIS_PADDING_PCT)
  }

  // ---- 拖拽交互 ----
  const trackRef = ref<HTMLElement | null>(null)
  const isDragging = ref(false)

  function getValueFromEvent(e: MouseEvent | TouchEvent) {
    if (!trackRef.value) return
    const rect = trackRef.value.getBoundingClientRect()
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
    const padPx = (AXIS_PADDING_PCT / 100) * rect.width
    const effectiveX = Math.max(0, Math.min(x - padPx, rect.width - 2 * padPx))
    const pct = effectiveX / (rect.width - 2 * padPx)
    ctx.actions.setPointerSpeed(Math.round(min + pct * (max - min)))
  }

  function onPointerDown(e: MouseEvent) {
    isDragging.value = true
    getValueFromEvent(e)
    document.addEventListener('mousemove', onPointerMove)
    document.addEventListener('mouseup', onPointerUp)
  }

  function onPointerMove(e: MouseEvent) {
    if (!isDragging.value) return
    getValueFromEvent(e)
  }

  function onPointerUp() {
    isDragging.value = false
    document.removeEventListener('mousemove', onPointerMove)
    document.removeEventListener('mouseup', onPointerUp)
  }
</script>

<style scoped></style>
